import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { isNational } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { Plus, MapPin, Clock, Copy, CalendarDays, Trash2, Users, CheckCircle2, DollarSign, Download, Pencil } from "lucide-react";
import RegistrantManagerDialog from "@/components/RegistrantManagerDialog";

const SECTIONS = ["Cubs", "Scouts", "Senior Scouts", "Rovers"];
const LEADER_ROLES = ["developer", "national_admin", "regional_admin", "chapter_leader", "scout_leader", "cubs_leader", "patrol_leader", "patrol_co_leader"];
const LEVEL_STYLE = {
  national: { bg: "hsl(32 87% 67%)", fg: "hsl(155 60% 8%)", label: "National" },
  regional: { bg: "hsl(12 65% 63%)", fg: "white", label: "Regional" },
  chapter: { bg: "hsl(149 40% 30%)", fg: "white", label: "Chapter" },
};

const emptyForm = {
  title: "", title_hy: "", description: "", date: "", end_date: "", start_time: "10:00",
  end_time: "13:00", location: "", section: "Scouts", sections: ["Scouts"],
  level: "chapter", chapter_id: "", expected_participants: 20, capacity: 0, waitlist_enabled: false,
  materials: "", objectives: "", activities: [], fee: 0, currency: "amd", prerequisites: "",
  checklist_items: [], manager_user_id: "",
};

export default function Programs() {
  const { user } = useAuth();
  const { t } = useT();
  const [programs, setPrograms] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [myRegs, setMyRegs] = useState({});
  const [managing, setManaging] = useState(null);
  const [checklistDraft, setChecklistDraft] = useState("");

  const load = async () => {
    const [{ data }, chRes, stRes] = await Promise.all([
      api.get("/programs"),
      api.get("/chapters").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setPrograms(data);
    setChapters(chRes.data || []);
    setStaff((stRes.data || []).filter(u => ["national_admin","developer","regional_admin","chapter_leader","scout_leader","cubs_leader","patrol_leader"].includes(u.role) && u.status === "active"));
    if (user?.role === "scout") {
      const map = {};
      await Promise.all(data.map(async p => {
        try {
          const r = await api.get(`/programs/${p.program_id}/my-registration`);
          if (r.data.status && r.data.status !== "none") map[p.program_id] = r.data.status;
        } catch (err) { console.warn(`my-registration lookup failed for ${p.program_id}:`, err?.message); }
      }));
      setMyRegs(map);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const canManage = user?.role && LEADER_ROLES.includes(user.role);
  const canPickLevel = isNational(user);

  const save = async () => {
    if (form.level === "chapter" && canPickLevel && !form.chapter_id) {
      toast.error("Please pick the chapter running this program");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/programs/${editingId}`, form);
        toast.success("Program updated");
      } else {
        await api.post("/programs", form);
        toast.success("Program created");
      }
      setOpen(false); setEditingId(null); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.program_id);
    setForm({
      ...emptyForm,
      ...p,
      sections: p.sections || [p.section || "Scouts"],
      checklist_items: p.checklist_items || [],
      end_date: p.end_date || "",
      manager_user_id: p.manager_user_id || "",
    });
    setOpen(true);
  };
  const dup = async (id) => {
    try { await api.post(`/programs/${id}/duplicate`); toast.success("Duplicated"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    try { await api.delete(`/programs/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const downloadRegistrants = async (p) => {
    try {
      const { data } = await api.get(`/programs/${p.program_id}/registrations`);
      if (!data || data.length === 0) {
        toast("No registrations yet for this program");
        return;
      }
      const XLSX = await import("xlsx");
      const headers = [
        "full_name", "full_name_hy", "email", "phone", "dob", "gender",
        "section", "patrol", "position", "chapter_name",
        "guardian_name", "guardian_phone", "parent_email", "emergency_contact",
        "membership_start", "registration_status", "paid", "registered_at",
      ];
      const rows = data.map(r => ({
        full_name: r.member?.full_name || "",
        full_name_hy: r.member?.full_name_hy || "",
        email: r.member?.email || "",
        phone: r.member?.phone || "",
        dob: r.member?.dob || "",
        gender: r.member?.gender || "",
        section: r.member?.section || "",
        patrol: r.member?.patrol || "",
        position: r.member?.position || "",
        chapter_name: r.member?.chapter_name || "",
        guardian_name: r.member?.guardian_name || "",
        guardian_phone: r.member?.guardian_phone || "",
        parent_email: r.member?.parent_email || "",
        emergency_contact: r.member?.emergency_contact || "",
        membership_start: r.member?.membership_start || "",
        registration_status: r.status || "",
        paid: r.paid ? "Yes" : (Number(p.fee || 0) > 0 ? "No" : "Free"),
        registered_at: r.created_at ? r.created_at.slice(0, 19).replace("T", " ") : "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
      ws["!cols"] = headers.map(h => ({ wch: Math.max(16, h.length + 2) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registrants");
      const date = new Date().toISOString().slice(0, 10);
      const safeTitle = (p.title || "program").replace(/[^a-z0-9-]+/gi, "_").slice(0, 40);
      XLSX.writeFile(wb, `registrants-${safeTitle}-${date}.xlsx`);
      toast.success(`Downloaded ${rows.length} registrant${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download");
    }
  };
  const register = async (p) => {
    try {
      const fee = Number(p.fee || 0);
      if (fee > 0) {
        const { data } = await api.post("/payments/programs/checkout", {
          program_id: p.program_id,
          origin_url: window.location.origin,
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast.error("Could not start checkout");
        }
        return;
      }
      const { data } = await api.post(`/programs/${p.program_id}/register`);
      setMyRegs({ ...myRegs, [p.program_id]: data.status });
      if (data.status === "waitlisted") toast(`You're on the waitlist for ${p.title}`);
      else toast.success(`Registered for ${p.title}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unregister = async (p) => {
    try {
      await api.delete(`/programs/${p.program_id}/register`);
      const m = { ...myRegs }; delete m[p.program_id];
      setMyRegs(m);
      toast.success("You've been removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleSection = (s) => {
    const has = form.sections.includes(s);
    setForm({ ...form, sections: has ? form.sections.filter(x => x !== s) : [...form.sections, s] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">{t("Programs & Activities", "Ծրագրեր և գործունեություն")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("Programs", "Ծրագրեր")}</h1>
          <p className="text-muted-foreground mt-1">{t("National, regional and chapter programs.", "Ազգային, տարածաշրջանային և մասնաճյուղի ծրագրեր։")}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button className="btn-pill bg-[hsl(12,65%,63%)]" onClick={openNew} data-testid="new-program-btn"><Plus size={16} className="mr-2"/>{t("New Program", "Նոր ծրագիր")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingId ? t("Edit Program", "Խմբագրել ծրագիր") : t("New Program", "Նոր ծրագիր")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="prg-title"/></div>
                <div className="col-span-2"><Label>Title (Armenian)</Label><Input value={form.title_hy} onChange={e => setForm({...form, title_hy: e.target.value})}/></div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></div>
                <div><Label>Start date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="prg-date"/></div>
                <div><Label>End date <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label><Input type="date" value={form.end_date || ""} min={form.date || undefined} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="prg-end-date"/></div>
                <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})}/></div>
                <div><Label>Start time</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}/></div>
                <div><Label>End time</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})}/></div>

                {canPickLevel && (
                  <div className="col-span-2">
                    <Label>Level</Label>
                    <div className="flex gap-2 mt-2">
                      {Object.entries(LEVEL_STYLE).map(([k, v]) => (
                        <button
                          key={k} type="button"
                          onClick={() => setForm({...form, level: k, chapter_id: k === "chapter" ? form.chapter_id : ""})}
                          className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold transition-colors ${form.level === k ? "text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                          style={form.level === k ? { background: v.bg, color: v.fg } : {}}
                          data-testid={`prg-level-${k}`}
                        >{v.label}</button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Chapter-level programs are visible only to that chapter; regional and national are visible to everyone.</p>
                  </div>
                )}

                {canPickLevel && form.level === "chapter" && (
                  <div className="col-span-2">
                    <Label>Chapter <span className="text-[hsl(0,65%,55%)]">*</span></Label>
                    <Select value={form.chapter_id || ""} onValueChange={v => setForm({...form, chapter_id: v})}>
                      <SelectTrigger data-testid="prg-chapter"><SelectValue placeholder="Select the chapter running this program"/></SelectTrigger>
                      <SelectContent>
                        {chapters.length === 0 && <div className="p-3 text-xs text-muted-foreground">No chapters yet — create one first.</div>}
                        {chapters.map(c => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.name}{c.short_name ? ` (${c.short_name})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="col-span-2">
                  <Label>Age sections (who is this for)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {SECTIONS.map(s => {
                      const active = form.sections.includes(s);
                      return (
                        <button
                          key={s} type="button"
                          onClick={() => toggleSection(s)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${active ? "bg-[hsl(149,40%,30%)] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                          data-testid={`prg-section-${s.replace(/\s/g,"-")}`}
                        >{s}</button>
                      );
                    })}
                  </div>
                </div>

                <div><Label>Capacity (0 = unlimited)</Label><Input type="number" min="0" value={form.capacity} onChange={e => setForm({...form, capacity: Number(e.target.value)})} data-testid="prg-capacity"/></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Switch checked={form.waitlist_enabled} onCheckedChange={v => setForm({...form, waitlist_enabled: v})} data-testid="prg-waitlist"/>
                    <span className="text-sm font-semibold">Enable waitlist</span>
                  </label>
                </div>

                <div className="col-span-2">
                  <Label>Fee (AMD ֏ — leave 0 for free events)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-muted-foreground">֏</span>
                    <Input type="number" min="0" step="1" value={form.fee} onChange={e => setForm({...form, fee: Number(e.target.value)})} data-testid="prg-fee"/>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Scouts pay via Stripe checkout before their spot is confirmed.</p>
                </div>

                <div className="col-span-2">
                  <Label>Prerequisites (optional)</Label>
                  <Textarea rows={3} placeholder="e.g. Must have completed First Aid badge, minimum age 12, guardian consent form" value={form.prerequisites || ""} onChange={e => setForm({...form, prerequisites: e.target.value})} data-testid="prg-prerequisites"/>
                  <p className="text-xs text-muted-foreground mt-1">Shown on the program page so scouts know what to prepare.</p>
                </div>

                <div><Label>Expected participants</Label><Input type="number" value={form.expected_participants} onChange={e => setForm({...form, expected_participants: Number(e.target.value)})}/></div>
                <div className="col-span-2"><Label>Materials</Label><Input value={form.materials} onChange={e => setForm({...form, materials: e.target.value})}/></div>
                <div className="col-span-2"><Label>Objectives</Label><Textarea value={form.objectives} onChange={e => setForm({...form, objectives: e.target.value})}/></div>

                <div className="col-span-2">
                  <Label>Event leader / manager <span className="text-muted-foreground font-normal text-xs">(optional — they can mark attendance and tick the achievements checklist)</span></Label>
                  <Select value={form.manager_user_id || "__none__"} onValueChange={v => setForm({...form, manager_user_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="prg-manager"><SelectValue placeholder="Pick a leader to run this event"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {staff.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label>Seminar achievements checklist <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <p className="text-xs text-muted-foreground mb-2">Items the leader ticks off per attendee during the seminar — e.g. "Knot tying", "First-aid demo", "Pledge recited".</p>
                  <div className="space-y-1.5 mb-2">
                    {form.checklist_items.map((item, i) => (
                      <div key={`${i}-${item}`} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/30">
                        <CheckCircle2 size={14} className="text-[hsl(149,40%,30%)] flex-shrink-0"/>
                        <span className="text-sm flex-1 truncate">{item}</span>
                        <button type="button" onClick={() => setForm({...form, checklist_items: form.checklist_items.filter((_, j) => j !== i)})} className="text-[hsl(0,65%,55%)] hover:bg-[hsl(0,65%,55%)]/10 rounded-full p-1" data-testid={`prg-checklist-remove-${i}`}><Trash2 size={13}/></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={checklistDraft}
                      onChange={e => setChecklistDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && checklistDraft.trim()) { e.preventDefault(); setForm({...form, checklist_items: [...form.checklist_items, checklistDraft.trim()]}); setChecklistDraft(""); } }}
                      placeholder="Add an achievement item…"
                      data-testid="prg-checklist-input"
                    />
                    <Button type="button" variant="outline" className="btn-pill" onClick={() => { if (checklistDraft.trim()) { setForm({...form, checklist_items: [...form.checklist_items, checklistDraft.trim()]}); setChecklistDraft(""); } }} data-testid="prg-checklist-add">
                      <Plus size={14} className="mr-1"/>Add
                    </Button>
                  </div>
                </div>
              </div>
              <Button onClick={save} className="btn-pill w-full bg-[hsl(149,40%,30%)] mt-2" data-testid="prg-save">Create</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.map(p => {
          const lvl = LEVEL_STYLE[p.level || "chapter"] || LEVEL_STYLE.chapter;
          const secs = (p.sections && p.sections.length ? p.sections : [p.section]).filter(Boolean);
          const mine = myRegs[p.program_id];
          const registered = p.registered_count || 0;
          const cap = p.capacity || 0;
          const full = cap > 0 && registered >= cap;
          return (
            <Card key={p.program_id} className="clay-card overflow-hidden hover-lift">
              <div className="h-32 relative" style={{
                backgroundImage: "linear-gradient(135deg, hsl(149 40% 30% / 0.85), hsl(12 65% 63% / 0.5)), url('https://images.unsplash.com/photo-1600706843784-6f0ad251f52f?crop=entropy&cs=srgb&fm=jpg&q=85')",
                backgroundSize: "cover", backgroundPosition: "center",
              }}>
                <Badge className="absolute top-3 left-3 rounded-full" style={{ background: lvl.bg, color: lvl.fg }}>{lvl.label}</Badge>
                <div className="absolute top-3 right-3 flex flex-wrap justify-end gap-1 max-w-[65%]">
                  {secs.map(s => <Badge key={s} className="rounded-full bg-white/95 text-foreground text-[10px]">{s}</Badge>)}
                </div>
              </div>
              <div className="p-5">
                <Link to={`/programs/${p.program_id}`}>
                  <div className="font-display font-bold text-lg hover:text-[hsl(12,65%,63%)]" data-testid={`prg-title-${p.program_id}`}>{p.title}</div>
                </Link>
                <div className="text-xs text-muted-foreground">{p.title_hy}</div>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2"><CalendarDays size={12}/>{p.date}</div>
                  <div className="flex items-center gap-2"><Clock size={12}/>{p.start_time} – {p.end_time}</div>
                  <div className="flex items-center gap-2"><MapPin size={12}/>{p.location}</div>
                  {cap > 0 && (
                    <div className="flex items-center gap-2">
                      <Users size={12}/>
                      <span className={full ? "text-[hsl(0,65%,55%)] font-bold" : "font-semibold"}>{registered} / {cap}</span>
                      {p.waitlist_count > 0 && <span className="text-[hsl(32,87%,55%)]">· +{p.waitlist_count} waitlisted</span>}
                    </div>
                  )}
                  {Number(p.fee) > 0 && (
                    <div className="flex items-center gap-2 text-[hsl(12,65%,55%)] font-bold" data-testid={`prg-fee-${p.program_id}`}>
                      <DollarSign size={12}/> ֏{Number(p.fee).toLocaleString()} AMD
                    </div>
                  )}
                </div>

                {user?.role === "scout" && (
                  <div className="mt-4">
                    {mine === "registered" && (
                      <div className="flex items-center justify-between">
                        <Badge className="rounded-full bg-[hsl(149,40%,30%)]"><CheckCircle2 size={12} className="mr-1"/> You're in</Badge>
                        <Button size="sm" variant="ghost" onClick={() => unregister(p)} data-testid={`prg-unreg-${p.program_id}`}>Cancel</Button>
                      </div>
                    )}
                    {mine === "waitlisted" && (
                      <div className="flex items-center justify-between">
                        <Badge className="rounded-full bg-[hsl(32,87%,67%)] text-[hsl(155,60%,8%)]">Waitlisted</Badge>
                        <Button size="sm" variant="ghost" onClick={() => unregister(p)} data-testid={`prg-unreg-${p.program_id}`}>Leave</Button>
                      </div>
                    )}
                    {!mine && (
                      <Button size="sm" className="w-full btn-pill bg-[hsl(12,65%,63%)] hover:bg-[hsl(12,70%,55%)]" onClick={() => register(p)} disabled={full && !p.waitlist_enabled} data-testid={`prg-reg-${p.program_id}`}>
                        {full ? (p.waitlist_enabled ? "Join waitlist" : "Full") : (Number(p.fee) > 0 ? `Pay ֏${Number(p.fee).toLocaleString()} & register` : "Register")}
                      </Button>
                    )}
                  </div>
                )}

                {canManage && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-[hsl(32,87%,55%)] hover:bg-[hsl(32,87%,55%)]/10" data-testid={`edit-prg-${p.program_id}`}>
                      <Pencil size={12} className="mr-1"/>Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setManaging(p)} className="text-[hsl(149,40%,30%)] hover:bg-[hsl(149,40%,30%)]/10" data-testid={`manage-registrants-${p.program_id}`} title="Open registrant manager">
                      <Users size={12} className="mr-1"/>Registrants{typeof p.registered_count === "number" ? ` (${p.registered_count}${p.waitlist_count ? `+${p.waitlist_count}` : ""})` : ""}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadRegistrants(p)} data-testid={`dl-registrants-${p.program_id}`} title="Quick download Excel">
                      <Download size={12} className="mr-1"/>Excel
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dup(p.program_id)} data-testid={`dup-prg-${p.program_id}`}><Copy size={12} className="mr-1"/>Duplicate</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.program_id)} className="text-[hsl(0,65%,55%)] hover:bg-[hsl(0,65%,55%)]/10" data-testid={`del-prg-${p.program_id}`}><Trash2 size={12} className="mr-1"/>Delete</Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <RegistrantManagerDialog
        program={managing}
        open={!!managing}
        onClose={() => setManaging(null)}
        onChanged={load}
      />
    </div>
  );
}
